import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link as RouterLink, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { Loading } from './Loading';
import { BackDropLoad } from './LoadingBackDrop';
import { EngInfoHome } from './EngInfoHome';
import { SingleInfo } from './EngInfoSingleInfo';
import { UserContext, SnackbarContext, EngInfoContext } from './userContext';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline SVG icons ─────────── */

// Icons accept an explicit `color` prop and bake it directly onto the
// stroke/fill attribute. This bypasses CSS cascade entirely so the color
// always renders regardless of class compilation or inheritance issues.
const Icon = {
  ChevronDown: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Bookmark: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill={color} className={className} aria-hidden>
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  BookmarkBorder: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Alert: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  ),
  Close: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Book: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
};

/* ─────────── Modal (reused for bookmark confirm) ─────────── */

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/72 backdrop-blur-sm animate-[fadeIn_180ms_ease-out]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="
          relative w-full max-w-md rounded-2xl
          bg-surface-elevated border border-border-subtle
          p-6 sm:p-7
          animate-[fadeIn_180ms_ease-out]
          shadow-[0_24px_64px_-12px_rgba(0,0,0,0.8),0_0_64px_-8px_rgba(251,191,36,0.18)]
        "
      >
        {children}
      </div>
    </div>
  );
}

/* ─────────── Tree helpers ─────────── */

// API returns `info_data_id` as strings (e.g. "367") but `activeId` from the
// URL is parsed to a Number - so Map/Set lookups silently miss without coercion.
// Normalize every ID to a Number on the way in.
const idOf = (n) => Number(n.info_data_id);

function buildAncestorMap(nodes, parent = null, acc = new Map(), ancestors = []) {
  if (!nodes) return acc;
  for (const n of nodes) {
    const id = idOf(n);
    acc.set(id, ancestors);
    if (n.sub && n.sub.length) {
      buildAncestorMap(n.sub, id, acc, [...ancestors, id]);
    }
  }
  return acc;
}

/* ─────────── Tree node - amber bg theme, ink content ─────────── */

const TreeNode = ({ node, depth, activeId, openIds, onToggle, onBookmarkAction, isBookmarked, userLoggedIn, onNavigate }) => {
  const nodeId = Number(node.info_data_id);
  const hasKids = node.sub && node.sub.length > 0;
  const isOpen = openIds.has(nodeId);
  const isActive = activeId === nodeId;
  const bookmarked = isBookmarked(nodeId);

  return (
    <>
      <div
        className={`
          group relative flex items-center gap-1 rounded-lg
          pl-3 pr-1 py-[8px] my-[1px]
          transition-[background-color,transform] duration-200 ease-out-soft
          ${isActive
            ? 'bg-ink shadow-[0_4px_12px_-4px_rgba(10,10,11,0.4)]'
            : 'hover:bg-ink/[0.08]'}
        `}
      >
        {/* Animated active indicator - ink bar that grows in from left */}
        <span
          aria-hidden
          className={`
            pointer-events-none absolute -left-1 top-1/2 -translate-y-1/2
            h-6 rounded-r-full bg-ink
            transition-[width,opacity] duration-300 ease-out-soft
            ${isActive ? 'w-[3px] opacity-100' : 'w-0 opacity-0'}
          `}
        />

        <RouterLink
          to={`/eng-info/info-detail/${node.info_data_slug}/${node.info_data_id}`}
          onClick={onNavigate}
          style={{
            color: isActive
              ? '#FBBF24'
              : depth === 0
                ? '#0A0A0B'
                : 'rgba(10,10,11,0.78)',
            fontWeight: isActive ? 700 : depth === 0 ? 600 : 500,
          }}
          className={`
            flex-1 min-w-0 truncate text-[13px] leading-snug
            transition-[color,transform] duration-200 ease-out-soft
            ${isActive ? '' : 'group-hover:translate-x-0.5'}
          `}
        >
          {node.info_child_cat_ids}
        </RouterLink>

        {hasKids && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(nodeId); }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            aria-expanded={isOpen}
            className={`
              inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md
              transition-[background-color,transform] duration-200 ease-out-soft
              ${isActive ? 'hover:bg-amber/20' : 'hover:bg-ink/10'}
            `}
          >
            <Icon.ChevronDown
              color={isActive
                ? '#FBBF24'
                : isOpen
                  ? '#0A0A0B'
                  : 'rgba(10,10,11,0.55)'}
              className={`
                h-3.5 w-3.5
                transition-transform duration-300 ease-out-soft
                ${isOpen ? 'rotate-180' : ''}
              `}
            />
          </button>
        )}

        {!hasKids && userLoggedIn && (() => {
          const iconColor = isActive
            ? bookmarked ? '#FBBF24' : 'rgba(251,191,36,0.6)'
            : bookmarked ? '#0A0A0B' : 'rgba(10,10,11,0.55)';
          return (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBookmarkAction(node); }}
              aria-label={bookmarked ? 'Remove bookmark' : 'Add bookmark'}
              className={`
                flex h-6 w-6 shrink-0 items-center justify-center rounded
                transition-[background-color,opacity,transform] duration-200 ease-out-soft
                hover:scale-110
                ${bookmarked ? '' : 'opacity-0 group-hover:opacity-100'}
              `}
            >
              {bookmarked
                ? <Icon.Bookmark color={iconColor} className="h-[13px] w-[13px]" />
                : <Icon.BookmarkBorder color={iconColor} className="h-[13px] w-[13px]" />}
            </button>
          );
        })()}
      </div>

      {/* Smooth expand/collapse via grid-template-rows */}
      {hasKids && (
        <div
          className={`
            grid transition-[grid-template-rows] duration-300 ease-out-soft
            ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
          `}
        >
          <div className="overflow-hidden">
            <div className="pl-3 ml-2.5 my-[2px]" style={{ borderLeft: '1px solid rgba(10,10,11,0.18)' }}>
              {node.sub.map((child) => (
                <TreeNode
                  key={child.info_data_id}
                  node={child}
                  depth={depth + 1}
                  activeId={activeId}
                  openIds={openIds}
                  onToggle={onToggle}
                  onBookmarkAction={onBookmarkAction}
                  isBookmarked={isBookmarked}
                  userLoggedIn={userLoggedIn}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────── Hero ─────────── */

function EngInfoHero() {
  return (
    <section
      className="
        relative isolate overflow-hidden mt-3 mb-6
        rounded-2xl border border-border-subtle
        bg-gradient-to-br from-surface-paper to-surface-elevated
      "
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(900px 400px at 8% -10%, rgba(251,191,36,0.16), transparent 60%), radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.08), transparent 60%)' }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
        }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center px-5 sm:px-7 md:px-9 py-7 sm:py-8">
        <div className="min-w-0">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
            <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber">Reference library</span>
          </div>
          <h1 className="text-[28px] sm:text-[34px] md:text-[38px] font-bold leading-[1.05] tracking-[-0.03em] text-fg-primary">
            Engineering Info
          </h1>
          <div aria-hidden className="mt-3.5 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
          <p className="mt-3.5 max-w-[640px] text-[14px] leading-[1.6] text-fg-secondary">
            Construction norms, standards, registration procedures, government rates, and
            technical references - curated for industry professionals.
          </p>
        </div>

        <div className="hidden md:flex h-16 w-16 items-center justify-center rounded-2xl border border-amber/30 bg-gradient-to-br from-amber/24 to-amber/8 shrink-0 shadow-[0_0_24px_-4px_rgba(251,191,36,0.35)]">
          <Icon.Book className="h-8 w-8 text-amber" />
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ EngInfo ═══════════════════ */

export const EngInfo = () => {
  const history = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);
  const { engInfoList, setEngInfoList } = useContext(EngInfoContext);

  const [loading, setLoading] = useState(true);
  const [infoList, setInfoList] = useState([]);
  const [metadata, setMetadata] = useState(null);

  const [openIds, setOpenIds] = useState(() => new Set());
  const ancestorMap = useRef(new Map());

  const [bkDialog, setBkDialog] = useState({ open: false, node: null, action: 'add' });
  const [wait, setWait] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const match = location.pathname.match(/\/eng-info\/info-detail\/[^/]+\/(\d+)/);
  const activeId = match ? parseInt(match[1]) : null;

  // Auto-collapse mobile drawer when route changes
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Mobile drawer: lock body scroll + Esc to close
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    Axios.post('/new_apis/getsinglemetadata', { type: 'enginfo' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({
        seo_title: 'Engineering Info | ESAY',
        seo_desc: 'Construction norms, registrations, standards, and technical references.',
        seo_keywords: 'engineering info, construction norms',
      });
    });
    Axios.get('/new_apis/get-enginfo-list', { headers: { key } }).then((res) => {
      const list = res.data.infolist || [];
      setInfoList(list);
      ancestorMap.current = buildAncestorMap(list);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Auto-expand the sidebar tree to reveal the active topic - runs both when
  // activeId changes AND when infoList finally loads (so deep links work too).
  useEffect(() => {
    if (!activeId || !infoList.length) return;
    const ancestors = ancestorMap.current.get(activeId) || [];
    setOpenIds((prev) => {
      const next = new Set(prev);
      // include all ancestors AND the node itself, so its own kids show if any
      ancestors.forEach((a) => next.add(a));
      next.add(activeId);
      return next;
    });
  }, [activeId, infoList]);

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // engInfoList may contain string or number IDs depending on the API call that
  // populated it - coerce on both sides so lookups always succeed.
  const isBookmarked = (id) => {
    if (!engInfoList) return false;
    const n = Number(id);
    return engInfoList.some((x) => Number(x) === n);
  };

  const openBookmarkDialog = (node) => {
    if (!user) return;
    setBkDialog({
      open: true,
      node,
      action: isBookmarked(node.info_data_id) ? 'remove' : 'add',
    });
  };

  const closeBkDialog = () => setBkDialog({ open: false, node: null, action: 'add' });

  const confirmBookmark = () => {
    const node = bkDialog.node;
    const action = bkDialog.action;
    closeBkDialog();
    setWait(true);

    const url = action === 'add' ? '/new_apis/add_bookmark' : '/new_apis/delete_bookmark';
    const formData = { user_id: user.user_id, info_id: node.info_data_id };
    Axios.post(url, formData, { headers: { tkn: user.token } }).then((res) => {
      const s = res.data.status;
      if (s === 1 || s === '1') {
        setSnack({ message: res.data.msg, type: 'success', open: true });
        if (action === 'add') {
          engInfoList.push(node.info_data_id);
        } else {
          const idx = engInfoList.indexOf(node.info_data_id);
          if (idx >= 0) engInfoList.splice(idx, 1);
        }
        setEngInfoList([...engInfoList]);
        setWait(false);
      } else if (s === 3) {
        setWait(false);
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else if (s === 4) {
        setWait(false);
        setUser(null);
        history('/');
        localStorage.removeItem('estkn');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else {
        setWait(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    }).catch(() => setWait(false));
  };

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <BackDropLoad wait={wait} />

      {/* Wider container - reading experience, minimal side margin */}
      <div className="mx-auto w-full max-w-[1480px] px-3 sm:px-5 lg:px-6">
        <EngInfoHero />

        {/* Mobile-only "Browse topics" toggle */}
        <div className="md:hidden mb-3">
          <button
            type="button"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-expanded={mobileNavOpen}
            className={`
              flex w-full items-center justify-between gap-2 rounded-xl
              border bg-amber-gradient px-4 py-3
              text-[13px] font-bold uppercase tracking-[0.14em]
              text-ink
              transition-[border-color,box-shadow] duration-200 ease-out-soft
              ${mobileNavOpen
                ? 'border-amber/70 shadow-[0_0_0_3px_rgba(251,191,36,0.18)]'
                : 'border-amber/40 hover:border-amber/60'}
            `}
          >
            <span className="inline-flex items-center gap-2">
              <Icon.Book color="#0A0A0B" className="h-4 w-4" />
              {mobileNavOpen ? 'Hide topics' : 'Browse all topics'}
            </span>
            <Icon.ChevronDown
              color="#0A0A0B"
              className={`h-4 w-4 transition-transform duration-300 ease-out-soft ${mobileNavOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {/* Layout - 30/70 split on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,30%)_1fr] gap-4 md:gap-5 pb-10">
          {/* Desktop sidebar - sticky inline */}
          <aside
            className="
              relative isolate overflow-hidden rounded-xl
              bg-amber-gradient
              p-2.5
              md:sticky md:top-[68px] md:self-start
              md:max-h-[calc(100vh-92px)]
              shadow-[0_10px_28px_-12px_rgba(10,10,11,0.45)]
              hidden md:block
            "
          >
            {/* noise overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />
            <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-ink/35 to-transparent" />

            <div className="md:max-h-[calc(100vh-110px)] md:overflow-y-auto md:pr-1 -mr-1">
              {loading ? (
                <div className="px-2 py-2 flex flex-col gap-2.5">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-3 rounded animate-pulse"
                      style={{ width: `${55 + (i * 11) % 40}%`, animationDelay: `${i * 50}ms`, backgroundColor: 'rgba(10,10,11,0.18)' }}
                    />
                  ))}
                </div>
              ) : (
                <nav>
                  {infoList.map((cat) => (
                    <TreeNode
                      key={cat.info_data_id}
                      node={cat}
                      depth={0}
                      activeId={activeId}
                      openIds={openIds}
                      onToggle={toggleOpen}
                      onBookmarkAction={openBookmarkDialog}
                      isBookmarked={isBookmarked}
                      userLoggedIn={!!user}
                      onNavigate={() => setMobileNavOpen(false)}
                    />
                  ))}
                </nav>
              )}
            </div>
          </aside>

          {/* Content panel - dark/amber theme. SingleInfo wraps its body in a white card. */}
          <main className="min-w-0 rounded-xl border border-border-subtle bg-surface-paper p-5 sm:p-6 md:p-7">
            {loading ? <Loading /> : (
              <Routes>
                <Route path="info-detail/:slug?/:id?" element={<SingleInfo />} />
                <Route path="home" element={<EngInfoHome />} />
              </Routes>
            )}
          </main>
        </div>
      </div>

      {/* ═════ Mobile drawer - fixed top-layer (matches navbar + my-account drawer pattern) ═════ */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-[1200] md:hidden" role="dialog" aria-modal="true" aria-label="Engineering Info topics">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 bg-black/72 backdrop-blur-md animate-[fadeIn_180ms_ease-out]"
          />
          <aside
            className="
              absolute left-0 top-0 bottom-0 w-[85%] max-w-[340px]
              bg-amber-gradient
              flex flex-col
              animate-[fadeIn_220ms_ease-out]
              shadow-[24px_0_64px_-12px_rgba(0,0,0,0.8)]
            "
          >
            {/* noise overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-multiply"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />

            {/* Drawer header */}
            <div className="relative flex items-center justify-between gap-3 px-4 py-3 border-b border-ink/15">
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-ink text-amber">
                  <Icon.Book className="h-4 w-4" />
                </span>
                <span className="text-[14px] font-bold tracking-[-0.01em] text-ink">Topics</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
                className="
                  inline-flex h-9 w-9 items-center justify-center rounded-full
                  text-ink/70
                  transition-[background-color,color] duration-200
                  hover:bg-ink/10 hover:text-ink
                "
              >
                <Icon.Close className="h-4 w-4" />
              </button>
            </div>

            {/* Drawer body */}
            <div className="relative flex-1 overflow-y-auto p-2.5">
              {loading ? (
                <div className="px-2 py-2 flex flex-col gap-2.5">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-3 rounded animate-pulse"
                      style={{ width: `${55 + (i * 11) % 40}%`, animationDelay: `${i * 50}ms`, backgroundColor: 'rgba(10,10,11,0.18)' }}
                    />
                  ))}
                </div>
              ) : (
                <nav>
                  {infoList.map((cat) => (
                    <TreeNode
                      key={cat.info_data_id}
                      node={cat}
                      depth={0}
                      activeId={activeId}
                      openIds={openIds}
                      onToggle={toggleOpen}
                      onBookmarkAction={openBookmarkDialog}
                      isBookmarked={isBookmarked}
                      userLoggedIn={!!user}
                      onNavigate={() => setMobileNavOpen(false)}
                    />
                  ))}
                </nav>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Bookmark confirmation */}
      <Modal open={bkDialog.open} onClose={closeBkDialog}>
        <button
          type="button"
          onClick={closeBkDialog}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md text-fg-tertiary hover:bg-surface-overlay hover:text-fg-primary transition-colors"
        >
          <Icon.Close className="h-4 w-4" />
        </button>

        <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
          <Icon.Alert className="h-3.5 w-3.5 text-amber" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Confirm</span>
        </div>
        <h3 className="mt-3 text-[20px] font-bold tracking-[-0.02em] text-fg-primary leading-tight">
          {bkDialog.action === 'add' ? 'Add to bookmarks?' : 'Remove from bookmarks?'}
        </h3>
        <p className="mt-1.5 text-[13.5px] text-fg-secondary leading-relaxed">
          {bkDialog.action === 'add'
            ? <>Save <span className="font-semibold text-fg-primary">&ldquo;{bkDialog.node?.info_child_cat_ids}&rdquo;</span> to your bookmarks for quick access.</>
            : <>Remove <span className="font-semibold text-fg-primary">&ldquo;{bkDialog.node?.info_child_cat_ids}&rdquo;</span> from your bookmarks.</>
          }
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={closeBkDialog}
            className="
              inline-flex h-10 items-center justify-center rounded-lg px-4
              text-[13px] font-semibold text-fg-secondary
              hover:bg-surface-overlay hover:text-fg-primary
              transition-colors duration-200
            "
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmBookmark}
            className="
              group inline-flex h-10 items-center justify-center gap-2 rounded-lg px-5
              text-[13px] font-bold uppercase tracking-[0.1em]
              bg-amber text-ink
              hover:bg-amber-light hover:shadow-amber-glow
              active:scale-[0.98]
              transition-all duration-200 ease-out-soft
            "
          >
            {bkDialog.action === 'add' ? 'Add' : 'Remove'}
          </button>
        </div>
      </Modal>
    </>
  );
};
